import { createContext, useEffect, useMemo } from 'react';
import { makeVar } from '@apollo/client';

import { useLazyQuery, useReactiveVar } from '@apollo/client/react';

import { APPLICANT_META } from 'queries/applicant';
import { CUSTOMER_OVERVIEW } from 'queries/customer';

import { CUSTOMER_TYPE } from 'utilities/constants';

import useRouteParams from 'hooks/useRouteParams';

const RECENTLY_VIEWED_KEY = 'mc-recently-viewed';
const RECENT_CUSTOMERS_KEY = 'customers';
const RECENT_APPLICATIONS_KEY = 'applications';

export class CustomerInfo {
  constructor(customerType = CUSTOMER_TYPE.CUSTOMER, info) {
    this.initialized = !!info;

    info = info || {};
    this.customerType = customerType;
    this.id = info.id || info.applicantId;
    this.accounts = info.accounts || [];
    this.accountIds = this.accounts.map((account) => account.id);
    this.firstName = info.contactInfo?.firstName || info.firstName;
    this.middleName = info.contactInfo?.middleName || info.middleName;
    this.lastName = info.contactInfo?.lastName || info.lastName;
    this.lastFourSSN = info.lastFourSsn || info.last4Ssn;
    this.dateOfBirth = info.birthDate || info.dateOfBirth;
    this.email = info.contactInfo?.email || info.contactDetails?.emailAddress;
    this.phoneNumber =
      info.contactInfo?.rawPhoneNumber || info.contactDetails?.phoneNumber;

    this._fullName = info;
    this._address = info;
  }

  set _fullName(info) {
    if (info?.contactInfo) {
      info = info.contactInfo;
    }

    const firstName = info.firstName || '';
    const lastName = info.lastName || '';

    this.fullName = `${firstName} ${lastName}`.trim();
  }

  set _address(info) {
    const address =
      info?.contactInfo?.address || info?.contactDetails?.address || {};
    this.address = {
      city: address.city || '',
      state: address.state || '',
      street1: address.address1 || address.street1 || '',
      street2: address.address2 || address.street2 || '',
      postalCode: address.postalCode || address.zip || '',
    };
  }
}

const recentlyViewedStorage = localStorage.getItem(RECENTLY_VIEWED_KEY);

const recentlyViewedVar = makeVar(
  recentlyViewedStorage
    ? JSON.parse(recentlyViewedStorage)
    : {
        [RECENT_CUSTOMERS_KEY]: [],
        [RECENT_APPLICATIONS_KEY]: [],
      },
);

const updateRecentlyViewed = async (key, id, label) => {
  const recentlyViewed = recentlyViewedVar();
  let viewedSet = recentlyViewed[key];

  const currentIndex = viewedSet.findIndex((rc) => rc.id === id);

  if (currentIndex === 0) {
    return;
  }

  if (currentIndex > 0) {
    viewedSet = [
      viewedSet[currentIndex],
      ...viewedSet.slice(0, currentIndex),
      ...viewedSet.slice(currentIndex + 1),
    ];
  } else {
    viewedSet.unshift({
      id,
      label,
    });
  }

  if (viewedSet.length > 5) {
    viewedSet.pop();
  }

  const update = {
    ...recentlyViewed,
    [key]: viewedSet,
  };

  recentlyViewedVar(update);
  localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(update));
};

export const CustomerInfoContext = createContext(null);

const CustomerInfoProvider = ({ children }) => {
  const recentlyViewed = useReactiveVar(recentlyViewedVar);

  const { customerId, applicationId } = useRouteParams();
  const activeCustomerType = applicationId
    ? CUSTOMER_TYPE.APPLICANT
    : CUSTOMER_TYPE.CUSTOMER;

  /**
   * Applicant Info
   */
  const [getApplicantMeta, applicantMetaQuery] = useLazyQuery(APPLICANT_META, {
    notifyOnNetworkStatusChange: true,
  });

  const applicantInfo = useMemo(() => {
    const applicants =
      applicantMetaQuery?.data?.applicationV2?.submission?.applicants || [];
    const applicant = applicants[0];
    let applicantInfo;

    if (applicant) {
      applicantInfo = new CustomerInfo(CUSTOMER_TYPE.APPLICANT, applicant);
      if (applicationId) {
        updateRecentlyViewed(
          RECENT_APPLICATIONS_KEY,
          applicationId,
          applicantInfo.fullName,
        );
      }
    }

    return applicantInfo || new CustomerInfo(CUSTOMER_TYPE.APPLICANT);
  }, [applicantMetaQuery.data]);

  /**
   * Customer Info
   */
  const [getCustomerMeta, customerMetaQuery] = useLazyQuery(CUSTOMER_OVERVIEW, {
    notifyOnNetworkStatusChange: true,
  });

  const customerInfo = useMemo(() => {
    const customer = customerMetaQuery?.data?.customer;
    let customerInfo;
    if (customer) {
      customerInfo = new CustomerInfo(CUSTOMER_TYPE.CUSTOMER, customer);
      updateRecentlyViewed(
        RECENT_CUSTOMERS_KEY,
        customerInfo.id,
        customerInfo.fullName,
      );
    }

    return customerInfo || new CustomerInfo(CUSTOMER_TYPE.CUSTOMER);
  }, [customerMetaQuery.data]);

  useEffect(() => {
    if (applicationId) {
      getApplicantMeta({ variables: { applicationId } });
    }
  }, [applicationId]);

  useEffect(() => {
    if (customerId) {
      getCustomerMeta({ variables: { customerId } });
    }
  }, [customerId]);

  return (
    <CustomerInfoContext.Provider
      value={{
        customerInfo:
          activeCustomerType === CUSTOMER_TYPE.APPLICANT
            ? applicantInfo
            : customerInfo,
        customerInfoQuery:
          activeCustomerType === CUSTOMER_TYPE.APPLICANT
            ? applicantMetaQuery
            : customerMetaQuery,
        recentApplications: recentlyViewed.applications,
        recentCustomers: recentlyViewed.customers,
      }}
    >
      {children}
    </CustomerInfoContext.Provider>
  );
};

export default CustomerInfoProvider;
